/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Drawer, Grid, Typography, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import TuneIcon from '@material-ui/icons/Tune';
import { Link } from 'gatsby';
import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { DATE_FORMAT, InvoiceActionsType } from '../..';
import {
  GetJobsInput,
  Job,
  JobLocation,
} from '../../../../../__generated__/pgeplus-types';
import { PageTextContentType } from '../../../../../hooks/useContentMgmt';
import routes from '../../../../../routes';
import colors from '../../../../../themes/main-colors';
import { toDateString } from '../../../../../util/format';
import { FullDivider } from '../../../../pge-plus-common/FullDivider';
import { Filter } from '../controls/Filter';
import { Sort } from '../controls/Sort';
import {
  InvoiceDetails,
  InvoiceDetailsParams,
} from '../../../../../__generated__/pge-types';
import {
  TableControlsActions,
  TableControlsType,
} from '../../../../../hooks/invoice-payment-portal/useInvoiceList';
import { PictureAsPdf } from '@material-ui/icons';
import TextLink from '../../../../text-link';

type MobileViewProps = {
  content: PageTextContentType;
  tableControls: TableControlsType;
  invoiceActions: InvoiceActionsType;
  invoices?: InvoiceDetails[];
  searchParams?: InvoiceDetailsParams;
  downloadBill: (encryptedBillId: string) =>  Promise<void>;
};

export const MobileView = (props: MobileViewProps) => {
  const {
    content,
    tableControls,
    invoices,
    invoiceActions,
    searchParams,
    downloadBill
  } = props;
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  return (
    <Fragment>
      <Box
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
        onClick={() => setOpenDrawer(true)}
      >
        <TuneIcon css={{ marginLeft: theme.spacing(1) }} />
      </Box>
      <FullDivider />
      <Fragment>
        <Box p={1}>
          {invoices?.map(invoice => {
            return (
              <Fragment key={invoice.billId!}>
                <Grid
                  css={{
                    padding: '32px 24px',
                    backgroundColor: colors.riverBlue,
                    borderLeft: `2px solid ${colors.noirBlur}`,
                  }}
                >
                  <Typography>
                  <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                    {`${content.get('DUE_DATE')}: `}
                    </span>
                    <span
                      css={{
                        fontWeight: 400,
                      }}
                    >
                      {toDateString(invoice.dueDate, DATE_FORMAT)}
                    </span>
                  </Typography>

                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('PROJECT_NUMBER')}: `}
                    </span>
                    <span>{invoice.projectNum}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('PROJECT_ADDRESS')}: `}
                    </span>
                    <span>{`${invoice.projectAddress}`}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('AMOUNT_DUE')}: `}
                    </span>
                    <span>${parseFloat(invoice?.amountDue?.toString()!).toFixed(2)}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('BILL_AMOUNT')}: `}
                    </span>
                    <span>${parseFloat(invoice?.billAmount?.toString()!).toFixed(2)}</span>
                  </Typography>
                  <Typography>
                    <span
                      css={{
                        fontWeight: 'bold',
                      }}
                    >
                      {`${content.get('REUSABLE_STATUS')}: `}
                    </span>
                    {invoice.status}
                  </Typography>
                  <Typography>
                  {invoiceActions(invoice)}
                  <PictureAsPdf style={{ marginRight: 4, fontSize: 12 }} />
                             <TextLink
                               style={{
                                 textDecoration: 'underline',
                                 marginRight: '10px',
                                 fontSize: '12px',
                                 display:'inline-block'
                               }}
                               onClick={async (e: any) => {
                                 e.preventDefault();
                                 await downloadBill(
                                  invoice.encryptedBillId || ''
                                 );
                               }}
                             >
                              {content.get('DOWNLOAD_PDF')}
                             </TextLink>
                  </Typography>
                </Grid>
              </Fragment>
            );
          })}
        </Box>
      </Fragment>
      <Drawer
        anchor="bottom"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
      >
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            margin: theme.spacing(1),
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <CloseIcon />
        </Box>
        <Box>
          <Box
            css={{
              marginBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
          >
            <Typography
              css={{
                fontSize: theme.spacing(2),
                fontWeight: 'bold',
              }}
            >
              {content.get('FILTER_BY_STATUS')}
            </Typography>
            <Filter
              content={content}
              filter={val => tableControls(TableControlsActions.FILTER, val)}
              setOpenDrawer={setOpenDrawer}
              value={searchParams?.status || undefined}
            />
          </Box>
          <FullDivider />
          <Box
            css={{
              marginBottom: theme.spacing(2),
              paddingLeft: theme.spacing(2),
              paddingRight: theme.spacing(2),
            }}
          >
            <Typography
              css={{
                fontSize: theme.spacing(2),
                fontWeight: 'bold',
              }}
            >
              {content.get('SORT_BY')}
            </Typography>
            <Sort
              content={content}
              sort={val => tableControls(TableControlsActions.SORT, val)}
              value={searchParams?.sort || undefined}
            />
          </Box>
          <FullDivider />
        </Box>
      </Drawer>
    </Fragment>
  );
};
